
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import logger from "@/utils/logger";
import InvalidResetLink from "@/components/auth/InvalidResetLink";
import EnhancedLoadingState from "@/components/ui/EnhancedLoadingState";
import {
  safeToastError,
  safeToastSuccess,
  safeToastInfo,
} from "@/components/ui/toastManager";
import { validatePassword } from "@/utils/security";
import { sanitizeErrorMessage } from "@/utils/secureErrorHandler";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLinkValid, setIsLinkValid] = useState(true);
  const [isCheckingLink, setIsCheckingLink] = useState(true);
  // Ref invece di state: il flag serve solo al cleanup, non al render. Usando un
  // ref con effetto a deps vuote evitiamo la closure stantia che, al passaggio
  // false→true, faceva ripartire il cleanup precedente disconnettendo l'utente
  // anche a reset riuscito.
  const passwordResetCompletedRef = useRef(false);
  const navigate = useNavigate();
  const { theme } = useThemeConfig();

  // Se l'utente lascia la pagina senza aggiornare la password, viene disconnesso.
  // Deps vuote: il cleanup parte solo allo smontaggio reale e legge il valore
  // corrente dal ref.
  useEffect(() => {
    return () => {
      if (!passwordResetCompletedRef.current) {
        supabase.auth.signOut({ scope: 'local' }).catch(console.error);
      }
    };
  }, []);

  // Verifica il link di recovery tramite il flusso PKCE.
  // Non accettiamo più token grezzi dall'URL (rischio session fixation): il
  // `code` PKCE viene scambiato automaticamente dal client (detectSessionInUrl)
  // e qui ci limitiamo a rilevare la sessione di recovery che ne risulta. Lo
  // scambio richiede il code_verifier salvato sul device che ha avviato la
  // richiesta, quindi un code forgiato da terzi non stabilisce alcuna sessione.
  useEffect(() => {
    let settled = false;

    const finish = (valid: boolean) => {
      if (settled) return;
      settled = true;
      // Ripulisce l'URL così il code non resta esposto nella history.
      window.history.replaceState({}, "", window.location.pathname);
      if (valid) {
        safeToastInfo("Link verificato, puoi impostare una nuova password");
      } else {
        safeToastError("Link di reimpostazione non valido o mancante");
      }
      setIsLinkValid(valid);
      setIsCheckingLink(false);
    };

    const queryParams = new URLSearchParams(window.location.search);
    const errorParam =
      queryParams.get("error_description") ||
      queryParams.get("error_code") ||
      queryParams.get("error");
    if (errorParam) {
      logger.error("Link di reset con errore dal provider", errorParam);
      finish(false);
      return;
    }

    // La sessione di recovery può essere già stata stabilita dall'auto-exchange
    // (se il code è atterrato qui o su un'altra rotta) oppure arrivare a breve.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) finish(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) finish(true);
    });
    // Rete di sicurezza: se non si materializza una sessione di recovery, il
    // link non è valido (es. code mancante, scaduto o aperto su altro device).
    const timer = setTimeout(() => finish(false), 7000);

    return () => {
      clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      safeToastError("Le password non coincidono");
      return;
    }

    const pwCheck = validatePassword(password);
    if (!pwCheck.isValid) {
      safeToastError(pwCheck.error || "Password non valida");
      return;
    }

    setIsSubmitting(true);

    try {
      safeToastInfo("Aggiornamento in corso...");
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        throw error;
      }

      passwordResetCompletedRef.current = true;
      safeToastSuccess("Password aggiornata", {
        description: "Accedi con la nuova password.",
      });
      navigate("/password-reset-success");
    } catch (error: unknown) {
      console.error("Errore nel reset della password:", error);
      safeToastError(sanitizeErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputFocus = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    // Mantiene la pagina centrata quando la tastiera virtuale è aperta
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  return (
    <div className="min-viewport-height overflow-y-auto flex flex-col p-6 pb-safe bg-background text-foreground" style={{
      backgroundColor: theme.backgroundColor || undefined,
      color: theme.textColor || undefined
    }}>
      <div className="mx-auto w-full max-w-md">
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold"
            style={{ color: theme.sectionTitleColor }}
          >
            Ceralia - Spesa Semplice
          </h1>
          <p className="mt-2" style={{ color: theme.sectionTextColor }}>
            Reimposta la tua password
          </p>
        </div>

        <div
          className="p-6 rounded-lg shadow-sm"
          style={{
            backgroundColor: theme.cardBackgroundColor,
            color: theme.textColor,
            borderRadius: theme.borderRadius || "0.5rem",
            padding: theme.spacingBase || "1rem",
          }}
        >
          {isCheckingLink ? (
            <EnhancedLoadingState isLoading loadingMessage="Verifica del link..." skeletonCount={0} />
          ) : isLinkValid ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="password">Nuova Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1"
                  disabled={isSubmitting}
                  onFocus={handleInputFocus}
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1"
                  disabled={isSubmitting}
                  onFocus={handleInputFocus}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{
                  backgroundColor: theme.primaryColor || '#4caf50',
                  borderRadius: theme.buttonBorderRadius,
                  padding: theme.buttonPadding,
                  fontWeight: theme.fontWeightNormal || '400',
                  transitionDuration: theme.transitionSpeed || '0.2s'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Aggiornamento..." : "Aggiorna Password"}
              </Button>

              <div className="text-center mt-4">
                <Link
                  to="/login"
                  className="text-sm hover:underline"
                  style={{
                    color: theme.primaryColor || '#4caf50'
                  }}
                >
                  Torna al login
                </Link>
              </div>
            </form>
          ) : (
            <InvalidResetLink />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
